import { useEffect, ChangeEvent, useState } from 'react';
import {
	Button,
	Card,
	Col,
	Flex,
	Input,
	Popconfirm,
	Radio,
	Row,
	Skeleton,
	Switch,
	Table,
	Tooltip,
	Typography,
	Upload,
	RadioChangeEvent,
	message,
} from 'antd';
import Paragraph from 'antd/lib/typography/Paragraph';
import { Link } from 'react-router-dom';
import { STAFF_PORTAL_URL } from '../../config/constants.ts';
import dayjs from 'dayjs';
import {
	DeleteOutlined,
	EditOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import {
	useDeleteMaterialCertificateFileMutation,
	useGetMaterialByIdQuery,
	useSetMaterialCertificateNotAvailableMutation,
	useUpdateMaterialMutation,
	useUploadMaterialCertificateFileMutation,
} from '../../redux/api/material.ts';
import {
	Material,
	MaterialLength,
	MaterialReservation,
} from '../../interfaces/MaterialInventory.interface';
import MaterialInventoryLengths from './MaterialInventoryLengths.tsx';
import { Lineitem } from '../../interfaces/Ordel.Interface';

interface Props {
	material_id: string | number;
	showMaterialCertificatesPane?: boolean;
	refetchData: () => void;
	setSelectedMaterial: (material: Material) => void;
	setSelectedMaterialLength: (material_length: MaterialLength | null) => void;
	setSelectedMaterialReservation: (
		material_length: MaterialReservation
	) => void;
	setSelectedMaterialDocument: (doc: any) => void;
	setIsMaterialReservationModalOpen: (value: boolean) => void;
	selectedMaterialDocument: any;
	setIsReturnRemnantsModalOpen: (open: boolean) => void;
	showReturnRemnantButton: boolean;
	isMaterialReservationModalOpen: boolean;
	selectedMaterialLength: MaterialLength | null;
	lineItem?: Lineitem;
	material_cut_length?: string | null;
	saw_kerf?: number;
	material_cut_quantity?: number;
	forceRefetch?: boolean;
}

const { Text } = Typography;

// Helper function to format decimal values
const formatDecimal = (value: string | number | undefined | null, decimals = 2): string => {
	if (value === null || value === undefined || value === '') {
		return '---';
	}
	const numValue = typeof value === 'string' ? parseFloat(value) : value;
	if (isNaN(numValue)) {
		return '---';
	}
	return numValue.toFixed(decimals);
};

// Helper function to format currency values
const formatCurrency = (value: string | number | undefined | null): string => {
	if (value === null || value === undefined || value === '') {
		return '---';
	}
	const numValue = typeof value === 'string' ? parseFloat(value) : value;
	if (isNaN(numValue)) {
		return '---';
	}
	return `$${numValue.toFixed(2)}`;
};

const MaterialInventoryDetails = (props: Props) => {
	const [customValue, setCustomValue] = useState('');

	const { data, isLoading, isFetching, refetch } = useGetMaterialByIdQuery(
		{ id: props.material_id },
		{
			skip: !props.material_id,
			refetchOnMountOrArgChange: true,
		}
	);

	useEffect(() => {
		if (props?.forceRefetch) {
			refetch();
		}
	}, [props?.forceRefetch]);

	const [updateMaterial] = useUpdateMaterialMutation();

	const [uploadMaterialCertificateFile] =
		useUploadMaterialCertificateFileMutation();
	const [deleteMaterialCertificateFile] =
		useDeleteMaterialCertificateFileMutation();
	const [setCertificateNotAvailable, { isLoading: isSettingNoCert }] =
		useSetMaterialCertificateNotAvailableMutation();

	const handleChange = (e: RadioChangeEvent) => {
		const value = e.target.value;
		props.setSelectedMaterialDocument(value);
		if (value !== 'Other') {
			setCustomValue('');
		}
	};
	const handleUploadMaterialCertificate = async (
		id: string,
		file: any,
		type: string
	) => {
		const formData = new FormData();
		// formData.append('materials', id);
		formData.append('file', file);
		formData.append('type', type);
		await uploadMaterialCertificateFile({
			id,
			formData,
		})
			.unwrap()
			.then((response) => {
				if (response?.certificate_not_available_cleared) {
					message.info('Mill certificate on file: "No mill certificate available" cleared.');
				}
				if (response?.packet_layer_sync_failed) {
					message.warning(
						'The material was updated, but an open traveler packet did not refresh. Run the audit again.'
					);
				}
				refetch();
				props?.refetchData();
			});
	};

	const handleToggleCertificateNotAvailable = async (materialId: string, value: boolean) => {
		try {
			const response = await setCertificateNotAvailable({ id: materialId, value }).unwrap();
			message.success(
				value
					? 'Marked: no mill certificate available.'
					: 'Cleared: a mill certificate is expected again.'
			);
			if (response?.packet_layer_sync_failed) {
				message.warning(
					'The material was updated, but an open traveler packet did not refresh. Run the audit again.'
				);
			}
			refetch();
			props?.refetchData();
		} catch (error: any) {
			message.error(error?.data?.detail || 'Could not update the certificate flag.');
		}
	};

	const handleDeleteMaterialCertificateFile = async (id: string) => {
		await deleteMaterialCertificateFile({ id })
			.unwrap()
			.then((response) => {
				if (response?.packet_layer_sync_failed) {
					message.warning(
						'The material was updated, but an open traveler packet did not refresh. Run the audit again.'
					);
				}
				refetch();
				props?.refetchData();
			});
	};

	const material: Material | null = data ?? null;

	if (!material) {
		return <Skeleton />;
	}

	// Clearing stays possible; only marking a certified bar is blocked.
	const blockNoCert =
		!!material.has_a_material_certificate && !material.certificate_not_available;
	const scope =
		material.certificate_not_available_scope ??
		(material.original ? 'original' : material.material_order_line_item ? 'line' : 'bar');
	const noCertScope =
		scope === 'line'
			? `Applies to every bar from this PO line${
					material.purchase_order_number ? ` (${material.purchase_order_number})` : ''
				}, including remnants and future receipts.`
			: scope === 'original'
				? 'Applies to the original bar and all of its remnants.'
				: 'Applies to this bar.';

	return (
		<div className="flex flex-col w-full gap-3">
			<Row>
				<Col md={24} lg={24} xl={12}>
					{props.showMaterialCertificatesPane ? (
						<Card title="Details" className="w-full">
							<div className="flex flex-col gap-2">
								<p>
									<div className="flex flex-row gap-12">
										<div>
											<Text strong>Description</Text>
											<br />
											<Text>{material.display_name}</Text>
										</div>
										<div>
											<Text strong>P.O. Number</Text>
											<br />
											{material.purchase_order_number ? (
												<div className="flex items-center space-x-2">
													<Paragraph
													// copyable={{
													// 	text: material.purchase_order_name
													// 		? material.purchase_order_name
													// 		: material.purchase_order_id,
													// 	tooltips: ['Copy', 'Copied to clipboard'],
													// }}
													>
														<Link
															to={`${STAFF_PORTAL_URL}/purchase-orders/?search=${material.purchase_order_number}`}
															target="_blank"
															rel="noreferrer"
														>
															{material.purchase_order_number ?? '--'}
														</Link>
													</Paragraph>

													{/*<Button*/}
													{/*	hidden={true}*/}
													{/*	style={{ marginTop: -12 }}*/}
													{/*	type="link"*/}
													{/*	loading={downloadPoPdfResult.isLoading}*/}
													{/*	onClick={() => {*/}
													{/*		const newFormData = new FormData();*/}
													{/*		newFormData.append('poId', material.purchase_order_id);*/}
													{/*		downloadPoPdf(newFormData);*/}
													{/*	}}*/}
													{/*>*/}
													{/*	<DownloadOutlined />*/}
													{/*</Button>*/}
												</div>
											) : (
												'---'
											)}
										</div>

										{material.original ? (
											<div>
												<Text strong>Original</Text>
												<br />
												<Text>
													<Link
														to={`/material/materials/?search=${material.original.id}`}
													>
														{material.original.display_name}
													</Link>
												</Text>
											</div>
										) : null}
									</div>
								</p>
								<div className="grid grid-cols-4 gap-4">
									<div className="flex flex-col gap-2">
										<p>
											<Text strong>Vendor Part Number</Text>
											<br />
											<Text>{material.vendor_part_number ?? '---'}</Text>
										</p>
										<p>
											<Text strong>Vendor Description</Text>
											<br />
											<Text>{material.vendor_description ?? '---'}</Text>
										</p>
									</div>
									<div className="flex flex-col gap-2">
										<p>
											<Text strong>Volume</Text>
											<br />
											<Text>{formatDecimal(material.volume, 3)} in³</Text>
										</p>
										<p>
											<Text strong>Cost</Text>
											<br />
											<Text>{formatCurrency(material.cost)}</Text>
										</p>
									</div>
									<div className="flex flex-col gap-2">
										<p>
											<Text strong>Cost Per Cubic Inch</Text>
											<br />
											<Text>{formatCurrency(material.cost_per_cubic_inch)}</Text>
										</p>
										<p>
											<Text strong>Shipping Cost</Text>
											<br />
											<Text>{formatCurrency(material.shipping_cost)}</Text>
										</p>
									</div>
									<div className="flex flex-col gap-2">
										<p>
											<Text strong>Value</Text>
												<br />
											<Text>{formatCurrency(material.value)}</Text>
										</p>
										<p>
											<Text strong>Total Cost</Text>
											<br />
											<Text>{formatCurrency(material.total_cost)}</Text>
										</p>
									</div>
								</div>
							</div>

							{material.material_order_line_item ? (
								<>
									<Text strong>Material Order</Text>
									<br />
									<Link
										to={`/material/orders/${material.material_order_line_item.material_order}`}
									>
										View
									</Link>
								</>
							) : null}
							<br />

							<EditableTextArea
								initialText={material?.notes ?? ''}
								onSave={async (notes) =>
									await updateMaterial({
										id: material.id,
										values: { notes },
									})
										.unwrap()
										.then(() => props.refetchData())
								}
							/>
						</Card>
					) : null}
				</Col>
				<Col md={24} lg={24} xl={12}>
					{props.showMaterialCertificatesPane ? (
						<Card
							title={
								<div className="flex flex-row w-full justify-between items-baseline">
									<div>Material Documents</div>
									<Tooltip
										title={
											blockNoCert
												? 'This material has a mill certificate on file.'
												: undefined
										}
									>
										<Popconfirm
											disabled={blockNoCert}
											title={
												material.certificate_not_available
													? 'Clear "No mill certificate available"?'
													: 'Mark as "No mill certificate available"?'
											}
											description={
												<div style={{ maxWidth: 320 }}>
													{material.certificate_not_available
														? 'The traveler audit and quality packet will expect a cert again.'
														: 'The traveler audit and quality packet will stop waiting for a cert.'}
													<br />
													<Text type="secondary">{noCertScope}</Text>
												</div>
											}
											onConfirm={() =>
												handleToggleCertificateNotAvailable(
													material.id,
													!material.certificate_not_available
												)
											}
										>
											<Flex align="center" gap={6}>
												<Switch
													size="small"
													checked={!!material.certificate_not_available}
													loading={isSettingNoCert}
													disabled={blockNoCert}
												/>
												<Text style={{ fontWeight: 'normal' }}>
													No mill certificate available
												</Text>
											</Flex>
										</Popconfirm>
									</Tooltip>
								</div>
							}
							className="w-full"
							styles={{
								body: { padding: '1rem' },
								title: { width: '100%' },
							}}
						>
							{material.certificate_not_available ? (
								<Text type="secondary" className="block mb-2">
									Marked
									{material.certificate_not_available_by_name ||
									material.certificate_not_available_by_email
										? ` by ${
												material.certificate_not_available_by_name ||
												material.certificate_not_available_by_email
											}`
										: ''}
									{material.certificate_not_available_at
										? ` on ${dayjs(material.certificate_not_available_at).format('MM-DD-YYYY')}`
										: ''}
									{material.original ? ' (set on the original bar)' : ''}
								</Text>
							) : null}
							<Table
								size="small"
								columns={[
									{
										title: 'File',
										render: (cert) => (
											<Link
												to={cert.file}
												key={cert.id}
												target="_blank"
												rel="noreferer"
											>
												{cert.filename}
											</Link>
										),
									},
									{
										title: 'Upload Date',
										render: (cert) =>
											dayjs(cert.created_time).format('MM-DD-YYYY hh:mm:a'),
									},
									{
										title: 'Uploaded By',
										render: (cert) => cert.created_by?.full_name,
									},
									{
										title: 'Type',
										render: (cert) => cert.type,
									},
									{
										title: 'Actions',
										align: 'right',
										render: (cert) => (
											<Popconfirm
												title="Are you sure you want to delete this file?"
												onConfirm={() =>
													handleDeleteMaterialCertificateFile(cert.id)
												}
											>
												<Button type="text" icon={<DeleteOutlined />} />
											</Popconfirm>
										),
									},
								]}
								locale={{
									emptyText: <p className="py-2">No cert files uploaded</p>,
								}}
								pagination={false}
								dataSource={material.material_certificates}
							/>

							<div>
								<div className="p-4 bg-white rounded-lg shadow-md w-full">
									<div className="flex items-center space-x-4 mb-2">
										<h2 className="text-sm font-semibold">Select a type:</h2>
										<Radio.Group
											value={props.selectedMaterialDocument}
											onChange={handleChange}
										>
											<div className="flex space-x-4">
												<Radio value="Material Certificate">
													Material Certificate
												</Radio>
												<Radio value="Other">
													Other
													{props.selectedMaterialDocument === 'Other' && (
														<Input
															style={{ width: 200, marginLeft: 8 }}
															value={customValue}
															onChange={(e) => setCustomValue(e.target.value)}
															onBlur={() =>
																props.setSelectedMaterialDocument(customValue)
															}
															placeholder="Enter document type"
															size="small"
														/>
													)}
												</Radio>
											</div>
										</Radio.Group>
									</div>
								</div>
								<Upload.Dragger
									multiple={false}
									showUploadList={false}
									beforeUpload={() => false}
									onChange={(info) => {
										handleUploadMaterialCertificate(
											material.id,
											info.file,
											props.selectedMaterialDocument
										);
									}}
								>
									<p className="ant-upload-drag-icon">
										<UploadOutlined />
									</p>
									<p className="ant-upload-text">Drag files here to upload</p>
								</Upload.Dragger>
							</div>
						</Card>
					) : null}
				</Col>
			</Row>

			<Row>
				<Col md={24}>
					<MaterialInventoryLengths
						material={material}
						loading={isLoading || isFetching}
						refetchDataItem={refetch}
						refetchData={props.refetchData}
						setSelectedMaterial={props.setSelectedMaterial}
						setSelectedMaterialLength={props.setSelectedMaterialLength}
						setSelectedMaterialReservation={
							props.setSelectedMaterialReservation
						}
						setIsMaterialReservationModalOpen={
							props.setIsMaterialReservationModalOpen
						}
						setIsReturnRemnantsModalOpen={props.setIsReturnRemnantsModalOpen}
						showReturnRemnantButton={props.showReturnRemnantButton}
						isMaterialReservationModalOpen={
							props.isMaterialReservationModalOpen
						}
						selectedMaterialLength={props.selectedMaterialLength}
						lineItem={props?.lineItem}
						material_cut_length={props?.material_cut_length}
						saw_kerf={props?.saw_kerf}
						material_cut_quantity={props?.material_cut_quantity}
					/>
				</Col>
			</Row>
		</div>
	);
};

interface EditableTextAreaProps {
	initialText: string; // Optional prop for the initial text
	onSave: (notes: string) => void;
}

const EditableTextArea = (props: EditableTextAreaProps) => {
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [text, setText] = useState<string>(props.initialText);
	const [newText, setNewText] = useState<string>(props.initialText);
	const [expanded, setExpanded] = useState(false);

	const handleEdit = (): void => {
		setIsEditing(true);
	};

	const handleCancel = (): void => {
		setNewText(text);
		setIsEditing(false);
	};

	const handleSave = (): void => {
		setText(newText);
		setIsEditing(false);
		props.onSave(newText);
	};

	const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
		setNewText(e.target.value);
	};

	return (
		<div>
			<Flex align="center" className="gap-1">
				<Text strong>Notes</Text>
				{!isEditing ? (
					<Button
						type="text"
						onClick={handleEdit}
						size="small"
						icon={<EditOutlined />}
					>
						Edit
					</Button>
				) : null}
			</Flex>
			{!isEditing ? (
				<Typography.Paragraph
					style={{ whiteSpace: 'pre-wrap' }}
					ellipsis={{
						rows: 4,
						expandable: 'collapsible',
						expanded,
						onExpand: (_, info) => setExpanded(info.expanded),
					}}
				>
					{text}
				</Typography.Paragraph>
			) : (
				<div>
					<Input.TextArea
						value={newText}
						onChange={handleChange}
						rows={4}
						maxLength={512}
						showCount={true}
					/>
					<div className="mt-2">
						<Button
							type="primary"
							onClick={handleSave}
							style={{ marginRight: '6px' }}
						>
							Save
						</Button>
						<Button onClick={handleCancel}>Cancel</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default MaterialInventoryDetails;
